import React, { Component } from 'react';
import { useToggle, useAsync, } from 'react-use';
import { uniq, pick, isEmpty, last, sumBy, groupBy, keyBy, sortBy, omit, } from 'lodash';
import { Button } from 'reactstrap';
import Select from 'react-select';
import qs from 'qs';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { toast } from 'react-toastify';

import firebase, { functions } from '../firebase';
import { batch, getCollectionData, } from '../shared/firebase';
import { integerFormat } from '../util';
import { fields } from '../shared/models/linkingRule';
import { presetConsolidationAccountItems, } from '../shared/models/consolidationAccountItem';
import useCollectionSubscription from './hooks/useCollectionSubscription';
import useCompanySelector from './hooks/useCompanySelector';
import useQueryParams from './hooks/useQueryParams';
import LinkingRuleFormModal from './modals/LinkingRuleFormModal';
import ImportButton from './ImportButton';
import ExportButton from './ExportButton';
import CompanySyncButton from './CompanySyncButton';
import TrialsSyncButton from './TrialsSyncButton';
import AddButton from './AddButton';
import EditButton from './EditButton';
import DeleteButton from './DeleteButton';
import QuerySelector from './QuerySelector';

const db = firebase.firestore();

export default function LinkingRules (props) {
  const { company, relatedCompanies, accountItems, allConsolidationAccountItems, allConsolidationSegments, sectionsGroupedByRelatedCompanyId, } = props;
  const relatedCompaniesById = keyBy(relatedCompanies, 'id');
  const accountItemsGroupedByConsolidationAccountItemId = groupBy(accountItems, 'consolidationAccountItemId');
  const mappedConsolidationAccountItems = allConsolidationAccountItems.filter(_ => accountItemsGroupedByConsolidationAccountItemId[_.id]);
  const consolidationAccountItemsById = keyBy(allConsolidationAccountItems, 'id');
  const [isDragging, toggleDragging] = useToggle(false);
  const linkingRulesRef = company.ref.collection('linkingRules');
  const { items: linkingRules, isLoading, } = useCollectionSubscription(linkingRulesRef.orderBy('index'), [company], { detail: true });
  const linkingRulesById = keyBy(linkingRules, 'id');
  const rowsForExport = linkingRules.map(_ => pick(_, ['id', 'name']));
  const processRow = (batch, row, i) => {
    const { id, name, } = row;
    const ref = linkingRulesRef.doc(...(id ? [id] : []));
    const exists = linkingRulesById[id] != null;
    batch.set(ref, {
      name,
      ...(
        !exists && {
          index: (isEmpty(linkingRules) ? 0 : last(linkingRules).index) + i + 1,
          createdAt: new Date(),
        }
      ),
    }, { merge: true });
  };
  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMoveImmutable(linkingRules, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => {
      batch.update(company.ref.collection('linkingRules').doc(id), { index });
    });
  };
  const processRows = (rows) => {
    const linkingRuleNames = linkingRules.map(_ => _.name);
    if(
      rows.map(_ => _.name).some(_ => linkingRuleNames.includes(_)) ||
      uniq(rows.map(_ => _.name)).length !== rows.length
    ) {
      toast.error('名称が重複しています');
    } else {
      return rows;
    }
  };

  return props.translate(
    <div className="company-cf-change-types container">
      <div className="p-4 bg-white">
        <div className="mb-3 d-flex justify-content-end gap-1">
          <AddButton disabled={isLoading} itemRef={linkingRulesRef.doc()} processValues={_ => ({ ..._, index: linkingRules.length, })} FormModal={LinkingRuleFormModal} formProps={{ relatedCompanies, consolidationAccountItems: mappedConsolidationAccountItems, consolidationSegments: allConsolidationSegments, sectionsGroupedByRelatedCompanyId, }} />
        </div>
        <div>
          <table className="table sticky-table">
            <thead className="thead-light text-center">
              <tr>
                <th></th>
                <th style={{ minWidth: 200 }}>名称</th>
                <th style={{ minWidth: 200 }}>会社</th>
                <th style={{ minWidth: 200 }}>ルール</th>
                <th style={{ minWidth: 200 }}>説明</th>
                <th></th>
              </tr>
            </thead>
            <Container
              dragHandleSelector=".drag-handle"
              onDrop={onDrop}
              onDragStart={_ => toggleDragging(true)}
              onDragEnd={_ => toggleDragging(false)}
              dropPlaceholder={{ style: { background: 'eee', } }}
              render={(ref) => (
                <tbody ref={ref}>
                  {
                    linkingRules.map((linkingRule) => {
                      const { id, ref, name, relatedCompanyId1, relatedCompanyId2, items, description, } = linkingRule;
                      const relatedCompany1 = relatedCompaniesById[relatedCompanyId1];
                      const relatedCompany2 = relatedCompaniesById[relatedCompanyId2];
                      const relatedCompaniesByNumber = { 1: relatedCompany1, 2: relatedCompany2 };
                      const beforeDelete = async () => {
                        if(
                          allConsolidationAccountItems.some(_ => _.cfMappingItems?.some(_ => _.linkingRuleId === id)) ||
                          (await getCollectionData(company.ref.collection('cfChanges').where('linkingRuleIds', 'array-contains', id))).length > 0
                        ) {
                          toast.error('使用されているため、削除できません');
                          return false;
                        }
                      };

                      return (
                        <Draggable
                          key={id}
                          render={() => (
                            <tr style={{ display: !isDragging && 'table-row' }}>
                              <td style={{ width: 30 }}>
                                <div className="drag-handle text-muted cursor-pointer">
                                  <span className="fas fa-grip-vertical" />
                                </div>
                              </td>
                              <td style={{ minWidth: 200 }}>
                                {name}
                              </td>
                              <td>
                                <div>{relatedCompany1?.display_name}</div>
                                <div>{relatedCompany2?.display_name}</div>
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1">
                                  {
                                    items.slice(0, 2).map((item, i) => {
                                      const { isRequired, relatedCompanyNumber, consolidationAccountItemId, consolidationSegmentId, } = item;
                                      return (
                                        <div key={i} className="card p-2 d-block">
                                          {isRequired && <div className="badge badge-info">必須</div>}
                                          <div>
                                            <div>{relatedCompaniesByNumber[relatedCompanyNumber]?.display_name}</div>
                                            <div>{consolidationAccountItemsById[consolidationAccountItemId]?.name}</div>
                                          </div>
                                        </div>
                                      );
                                    })
                                  }
                                </div>
                                {
                                  items.length > 2 && (
                                    <div className="mt-1 small text-muted">
                                      他{items.length - 2}件のルール
                                    </div>
                                  )
                                }
                              </td>
                              <td style={{ whiteSpace: 'pre-line', wordBreak: 'break-all', }}>
                                {description}
                              </td>
                              <td className="text-nowrap text-right">
                                <EditButton itemRef={ref} FormModal={LinkingRuleFormModal} formProps={{ relatedCompanies, consolidationAccountItems: mappedConsolidationAccountItems, consolidationSegments: allConsolidationSegments, sectionsGroupedByRelatedCompanyId, }} />
                                <DeleteButton item={linkingRule} itemRef={ref} className="ml-2" beforeDelete={beforeDelete} />
                              </td>
                            </tr>
                          )}
                        />
                      );
                    })
                  }
                </tbody>
              )}
            />
          </table>
        </div>
      </div>
    </div>
  );
};
